import { useMemo, type ReactNode } from 'react';
import {
  applyReportFilters,
  resolveReportFilter,
  type ReportFilter,
} from '@erp/shared-types';
import type { ReportDataset } from '@/components/report-canvas/ReportDataContext';
import { ReportColorPaletteProvider } from '@/components/report-canvas/palette/ReportColorPaletteProvider';
import type { WorkbookReportData } from './useWorkbookReportData';
import { WorkbookReportServerContext } from './workbookReportRegistry';

/**
 * Report filters as both data paths apply them: date presets turned into
 * dates for today (the viewer's calendar day, sent to the server as dates).
 */
export function resolveReportFilters(
  filters: readonly ReportFilter[] | undefined,
  today: Date,
): ReportFilter[] {
  // The viewer's calendar day as a UTC date, which the presets count in.
  const day = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()),
  );
  return (filters ?? []).map((filter) => resolveReportFilter(filter, day));
}

/** Each dataset with only the rows passing the filters on its columns. */
export function filteredDatasets(
  datasets: readonly ReportDataset[],
  filters: readonly ReportFilter[],
): ReportDataset[] {
  return datasets.map((dataset) => {
    const rows = applyReportFilters(
      dataset.rows,
      filters,
      dataset.columns.map((column) => column.key),
    );
    return rows === dataset.rows
      ? dataset
      : { ...dataset, rows: [...rows], allRows: dataset.rows };
  });
}

/** The datasets a Report's components read under its filters, recomputed only when either changes. */
export function useFilteredDatasets(
  datasets: ReportDataset[],
  filters: readonly ReportFilter[],
): ReportDataset[] {
  return useMemo(
    () => (filters.length ? filteredDatasets(datasets, filters) : datasets),
    [datasets, filters],
  );
}

/**
 * What every component of a Workbook Report reads besides its rows: the
 * Report's color palette, and, for a dataset past the Client row limit, what
 * to ask the server for under the Report's filters.
 */
export function WorkbookReportScope({
  data,
  paletteId,
  filters,
  children,
}: {
  data: Pick<WorkbookReportData, 'workbookId' | 'sources'>;
  paletteId?: string;
  /** Resolved (see {@link resolveReportFilters}). */
  filters: readonly ReportFilter[];
  children: ReactNode;
}) {
  const server = useMemo(
    () => ({ workbookId: data.workbookId, sources: data.sources, filters }),
    [data.workbookId, data.sources, filters],
  );
  return (
    <ReportColorPaletteProvider paletteId={paletteId}>
      <WorkbookReportServerContext.Provider value={server}>
        {children}
      </WorkbookReportServerContext.Provider>
    </ReportColorPaletteProvider>
  );
}
